import React from 'react';
import { Box, Typography, Paper, IconButton, Tooltip } from '@mui/material';
import { styled } from '@mui/material/styles';
import DownloadIcon from '@mui/icons-material/Download';
import { exportMessageToMarkdown, exportSessionToMarkdown } from '../utils/exportMarkdown';

interface Message {
  id: string;
  role: 'user' | 'assistant' | 'system';
  content: string;
  timestamp: string;
}

interface MessageListProps {
  messages: Message[];
  sessionTitle?: string;
  sessionId?: string;
  reportOptions?: {
    length: string;
    detail: string;
    citation: string;
  };
}

const MessageBubble = styled(Paper)(({ theme, role }: { theme?: any; role: string }) => ({
  padding: theme.spacing(1.5, 2),
  marginBottom: theme.spacing(1.5),
  maxWidth: '80%',
  ...(role === 'user'
    ? {
        marginLeft: 'auto',
        backgroundColor: theme.palette.primary.main,
        color: theme.palette.primary.contrastText,
        borderRadius: '18px 18px 4px 18px',
      }
    : {
        marginRight: 'auto',
        backgroundColor: theme.palette.background.paper,
        border: `1px solid ${theme.palette.divider}`,
        borderRadius: '18px 18px 18px 4px',
      }),
}));

const MessageList: React.FC<MessageListProps> = ({
  messages,
  sessionTitle = 'Chat Export',
  sessionId = 'unknown',
  reportOptions
}) => {
  const handleExportMessage = (message: Message) => {
    exportMessageToMarkdown(message, sessionTitle);
  };

  const handleExportSession = () => {
    exportSessionToMarkdown({
      sessionTitle,
      sessionId,
      createdAt: messages.length > 0 ? messages[0].timestamp : new Date().toISOString(),
      messages,
      reportOptions,
    });
  };

  const formatContent = (content: string, role: string) => {
    if (role === 'user') {
      return content;
    }

    // For assistant messages, render as markdown-like HTML
    return (
      <Box
        className="markdown-content"
        sx={{
          '& h1, & h2, & h3': {
            mt: 1,
            mb: 0.5,
            fontWeight: 600,
            fontSize: '1.1em',
          },
          '& p': { mb: 1, lineHeight: 1.6 },
          '& ul, & ol': { ml: 2, mb: 1 },
          '& li': { mb: 0.25 },
          '& strong, & b': { fontWeight: 600 },
          '& em, & i': { fontStyle: 'italic' },
          '& code': {
            bgcolor: 'grey.100',
            px: 0.5,
            py: 0.25,
            borderRadius: 0.5,
            fontFamily: 'monospace',
            fontSize: '0.9em',
          },
          '& pre': {
            bgcolor: 'grey.100',
            p: 1,
            borderRadius: 0.5,
            overflow: 'auto',
            my: 1,
          },
          '& blockquote': {
            borderLeft: 3,
            borderColor: 'grey.300',
            pl: 1.5,
            fontStyle: 'italic',
            color: 'text.secondary',
          },
        }}
        dangerouslySetInnerHTML={{
          __html: content
            // Handle headers
            .replace(/^### (.+)$/gm, '<h3>$1</h3>')
            .replace(/^## (.+)$/gm, '<h2>$1</h2>')
            .replace(/^# (.+)$/gm, '<h1>$1</h1>')
            // Handle bold and italic
            .replace(/\*\*\*(.+?)\*\*\*/g, '<strong><em>$1</em></strong>')
            .replace(/\*\*(.+?)\*\*/g, '<strong>$1</strong>')
            .replace(/\*(.+?)\*/g, '<em>$1</em>')
            // Handle code blocks
            .replace(/```(\w*)\n([\s\S]+?)```/g, '<pre><code>$2</code></pre>')
            .replace(/`([^`]+)`/g, '<code>$1</code>')
            // Handle lists
            .replace(/^\- (.+)$/gm, '<li>$1</li>')
            .replace(/(<li>.+<\/li>\n?)+/g, '<ul>$&</ul>')
            // Handle line breaks
            .replace(/\n\n/g, '</p><p>')
            .replace(/\n/g, '<br />')
            // Wrap in paragraphs if not already wrapped
            .replace(/^(?!<)(.+)$/gm, '<p>$1</p>'),
        }}
      />
    );
  };

  return (
    <Box>
      {/* Export All Button */}
      {messages.length > 0 && (
        <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 2 }}>
          <Tooltip title="Export entire conversation to Markdown">
            <IconButton
              size="small"
              onClick={handleExportSession}
              sx={{
                bgcolor: 'action.hover',
                '&:hover': { bgcolor: 'action.selected' },
              }}
            >
              <DownloadIcon fontSize="small" />
              <Typography variant="caption" sx={{ ml: 0.5 }}>
                Export All
              </Typography>
            </IconButton>
          </Tooltip>
        </Box>
      )}

      {messages.map((message) => (
        <Box
          key={message.id}
          sx={{
            display: 'flex',
            justifyContent: message.role === 'user' ? 'flex-end' : 'flex-start',
            alignItems: 'flex-start',
          }}
        >
          <MessageBubble role={message.role}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
              <Box sx={{ flex: 1 }}>
                <Typography
                  variant="caption"
                  sx={{
                    display: 'block',
                    mb: 0.5,
                    opacity: 0.7,
                    fontSize: '0.7rem',
                  }}
                >
                  {message.role === 'user' ? 'You' : 'Research Assistant'}
                </Typography>
                {formatContent(message.content, message.role)}
              </Box>
              <Tooltip title="Export this message to Markdown">
                <IconButton
                  size="small"
                  onClick={() => handleExportMessage(message)}
                  sx={{
                    ml: 1,
                    opacity: 0.6,
                    '&:hover': { opacity: 1 },
                  }}
                >
                  <DownloadIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            </Box>
          </MessageBubble>
        </Box>
      ))}
    </Box>
  );
};

export default MessageList;
